import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProducts, imgUrl } from "@/lib/api";
import { MOTIF_LIST, Motif as MotifIcon } from "@/lib/motifs";
import { lifestyleFor } from "@/data/lifestyle";
import MotifCompanions from "@/components/product/MotifCompanions";

/**
 * MotifPreview — shows the CURRENT motif page layout plus 3 alternates
 * stacked vertically so the client can pick one.
 * URL: /motif-preview/:motif  (default: butterfly)
 */

const SectionHeader = ({ kicker, title, note }) => (
  <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-10 hairline-t">
    <div className="grid md:grid-cols-12 gap-6 items-end">
      <div className="md:col-span-8">
        <div className="nav-item mb-2" style={{ color: "var(--accent)" }}>{kicker}</div>
        <h2 className="serif text-3xl md:text-5xl leading-[1.05]">{title}</h2>
      </div>
      <p className="md:col-span-4 text-sm text-ink/70 leading-relaxed">{note}</p>
    </div>
  </div>
);

/* ============ VARIANT A · HERBARIUM ALBUM ============
   Torn kraft-paper specimen cards on a cotton-linen ground,
   each product pinned like a natural-history plate with a
   handwritten label strip beneath. */
const VariantHerbarium = ({ items, motif }) => (
  <section className="relative py-16 md:py-24" style={{ background: "#EFE7D5" }}>
    <SectionHeader
      kicker="A · Herbarium album"
      title="Pinned specimens on a linen page."
      note="Botanical-plate feel — each piece is a numbered specimen with a handwritten label. Great for the storytelling brand voice."
    />
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-6">
      <div className="relative grid grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-14">
        {items.map((p, i) => {
          const skew = ["-rotate-[1.2deg]", "rotate-[0.8deg]", "-rotate-[0.6deg]"][i % 3];
          return (
            <Link
              key={p.slug}
              to={`/product/${p.slug}`}
              className={`group relative block ${skew} transition-transform hover:rotate-0 hover:-translate-y-1 duration-500`}
              data-testid={`preview-a-tile-${p.slug}`}
            >
              {/* torn paper backing */}
              <div
                className="p-3 md:p-4"
                style={{
                  background: "#FBF5E6",
                  boxShadow: "0 20px 30px -14px rgba(28,26,25,0.28), 0 2px 4px rgba(28,26,25,0.08)",
                }}
              >
                {/* corner pins */}
                <span className="absolute -top-1 left-4 w-2.5 h-2.5 rounded-full" style={{ background: "#AE2B31", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
                <span className="absolute -top-1 right-4 w-2.5 h-2.5 rounded-full" style={{ background: "#AE2B31", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
                <div className="overflow-hidden">
                  <img
                    src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                    alt={p.name}
                    className="w-full aspect-[3/4] object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                  />
                </div>
                {/* handwritten label */}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-ink/50">Nº {String(i + 1).padStart(2, "0")}</span>
                  <MotifIcon name={motif} size={16} strokeColor="var(--accent)" />
                </div>
                <div
                  className="serif text-lg mt-1 leading-tight"
                  style={{ fontStyle: "italic", color: "var(--ink)" }}
                >
                  {p.name}
                </div>
                <div className="text-xs text-ink/60 mt-0.5">
                  {p.category === "women" ? "Adults · women" : p.category === "men" ? "Adults · men" : `Little ${p.category}`} · ₹{p.price.toLocaleString("en-IN")}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ VARIANT B · FILMSTRIP CINEMA ============
   Horizontal side-scrolling reel with sprocket-hole
   perforations and a big kicker text overlaid on each frame. */
const VariantFilmstrip = ({ items }) => (
  <section className="relative py-16 md:py-24" style={{ background: "#141210" }}>
    <div style={{ color: "var(--ground)" }}>
      <SectionHeader
        kicker="B · Filmstrip cinema"
        title="A reel of pieces, side-scrolled."
        note="Cinematic dark frame with side-scrolling reel and big serif titles overlaid. Feels like an editorial from Vogue."
      />
    </div>
    <div className="mt-8 relative">
      {/* top sprockets */}
      <div className="h-4 flex gap-4 px-6 md:px-12 lg:px-24 overflow-hidden">
        {Array.from({ length: 60 }).map((_, i) => (
          <span key={i} className="flex-none w-4 h-4 rounded-sm" style={{ background: "#EFE7D5" }} />
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-6 md:px-12 lg:px-24 py-4" data-testid="preview-b-reel">
        {items.map((p, i) => (
          <Link
            key={p.slug}
            to={`/product/${p.slug}`}
            className="relative flex-none w-[75vw] md:w-[520px] group"
            data-testid={`preview-b-tile-${p.slug}`}
          >
            <div className="relative overflow-hidden">
              <img
                src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                alt={p.name}
                className="w-full aspect-[3/4] object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.72) 100%)" }} />
              {/* label */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <div className="text-[10px] tracking-[0.28em] uppercase mb-2" style={{ color: "rgba(246,241,233,0.7)" }}>Reel Nº {String(i + 1).padStart(2, "0")}</div>
                <div className="serif text-3xl md:text-4xl leading-[1.05]" style={{ color: "var(--ground)" }}>
                  {p.name}
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <span className="serif text-lg" style={{ color: "var(--ground)" }}>₹{p.price.toLocaleString("en-IN")}</span>
                  <span className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "rgba(246,241,233,0.7)" }}>
                    See the piece →
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {/* bottom sprockets */}
      <div className="h-4 flex gap-4 px-6 md:px-12 lg:px-24 overflow-hidden">
        {Array.from({ length: 60 }).map((_, i) => (
          <span key={i} className="flex-none w-4 h-4 rounded-sm" style={{ background: "#EFE7D5" }} />
        ))}
      </div>
    </div>
  </section>
);

/* ============ VARIANT C · CONSTELLATION GRID ============
   Products arranged asymmetrically on a warm ivory sky
   with faint hairlines connecting them, tagged by category. */
const VariantConstellation = ({ items }) => {
  // 6-point constellation layout (percent-based on the container).
  const anchors = [
    { l: 14, t: 6  },
    { l: 62, t: 12 },
    { l: 34, t: 34 },
    { l: 78, t: 40 },
    { l: 18, t: 58 },
    { l: 60, t: 66 },
    { l: 42, t: 82 },
  ];
  // hairline edges between products (i -> j)
  const edges = [[0,2],[1,3],[2,4],[3,5],[4,6],[2,5]];

  return (
    <section className="relative py-16 md:py-24" style={{ background: "#F6F1E9" }}>
      <SectionHeader
        kicker="C · Constellation"
        title="Same motif, drawn across the sky."
        note="Products scattered like stars with hairline threads showing the motif crossing categories — Girls, Boys, Adults. Storytelling map."
      />
      <div className="mt-8 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="relative w-full" style={{ height: 1100 }}>
          {/* threads */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {edges.map(([i, j], k) => {
              if (!anchors[i] || !anchors[j] || !items[i] || !items[j]) return null;
              return (
                <line
                  key={k}
                  x1={anchors[i].l}
                  y1={anchors[i].t}
                  x2={anchors[j].l}
                  y2={anchors[j].t}
                  stroke="rgba(28,26,25,0.22)"
                  strokeWidth="0.08"
                  strokeDasharray="0.5 0.8"
                />
              );
            })}
          </svg>
          {items.slice(0, anchors.length).map((p, i) => (
            <Link
              key={p.slug}
              to={`/product/${p.slug}`}
              className="absolute group"
              style={{
                left: `${anchors[i].l}%`,
                top: `${anchors[i].t}%`,
                width: "26%",
                minWidth: 200,
                transform: "translate(-30%, -0%)",
              }}
              data-testid={`preview-c-tile-${p.slug}`}
            >
              <div className="relative overflow-hidden hairline">
                <img
                  src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                  alt={p.name}
                  className="w-full aspect-[3/4] object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                />
                {/* star dot */}
                <span className="absolute -top-2 -left-2 w-4 h-4 rounded-full" style={{ background: "var(--ground)", border: "1px solid var(--ink)" }}>
                  <span className="absolute inset-1 rounded-full" style={{ background: "var(--accent)" }} />
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[10px] tracking-[0.24em] uppercase text-ink/50">
                  {p.category === "women" ? "Adults · women" : p.category === "men" ? "Adults · men" : p.category}
                </div>
                <div className="serif text-lg md:text-xl leading-tight mt-1">{p.name}</div>
                <div className="text-xs text-ink/70">₹{p.price.toLocaleString("en-IN")}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ CURRENT (V0) — for comparison ============ */
const VariantCurrent = ({ items, motif, label }) => (
  <section className="relative pt-24 md:pt-40 pb-16" style={{ background: "var(--ground)" }}>
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid md:grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-16 items-end">
        <div className="md:col-span-8">
          <div className="nav-item mb-3 md:mb-4" style={{ color: "var(--accent)" }}>Current · asymmetric grid</div>
          <h1 className="serif text-4xl md:text-7xl leading-[1.02]">
            The <em className="italic">{label.toLowerCase()}</em>.
          </h1>
        </div>
        <p className="md:col-span-3 md:col-start-10 text-sm text-ink/70 leading-relaxed">
          The layout live today — 3-column staggered grid with product name and price under each piece.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
        {items.map((p, i) => (
          <Link key={p.slug} to={`/product/${p.slug}`} className={`group ${i % 3 === 1 ? "md:mt-16" : i % 3 === 2 ? "md:mt-32" : ""}`}>
            <div className="overflow-hidden bg-sand">
              <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name} className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
            </div>
            <div className="mt-4 flex items-baseline justify-between">
              <span className="serif text-xl md:text-2xl">{p.name}</span>
              <span className="text-sm text-ink/70">₹{p.price.toLocaleString("en-IN")}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const MotifPreview = () => {
  const params = useParams();
  const motif = params.motif || "butterfly";
  const [items, setItems] = useState([]);
  const label = (MOTIF_LIST.find((m) => m.key === motif) || {}).label || motif;

  useEffect(() => {
    fetchProducts({ motif }).then(setItems);
  }, [motif]);

  return (
    <div data-testid={`motif-preview-${motif}`}>
      {/* Companion flock */}
      <div className="absolute left-0 right-0 top-24 h-[520px] md:h-[620px] pointer-events-none" style={{ zIndex: 0 }}>
        <MotifCompanions motif={motif} />
      </div>
      <VariantCurrent items={items} motif={motif} label={label} />
      <VariantHerbarium items={items} motif={motif} />
      <VariantFilmstrip items={items} />
      <VariantConstellation items={items} />
      <div className="py-16 text-center hairline-t" style={{ background: "var(--ground)" }}>
        <div className="serif italic text-2xl">Which layout should we lock in?</div>
        <div className="text-sm text-ink/60 mt-2">Tell me A, B, C — or keep Current.</div>
      </div>
    </div>
  );
};

export default MotifPreview;
